import React, { useState } from "react";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faLandmark,
    faMoneyBillWave,
    faChartLine,
    faShieldAlt,
    faFileContract,
    faGavel,
    faScaleBalanced,
    faUsers,
    faHandshake,
    faLightbulb,
    faAward,
    faCheckCircle,
    faChevronRight,
    faQuoteLeft,
    faStar,
    faBalanceScale,
    faUserTie,
    faPhone,
    faEnvelope,
    faCalendarAlt,
    faCertificate,
    faBuilding,
    faGlobe,
    faLock,
    faFileSignature,
    faPercentage,
    faReceipt,
    faCoins,
    faClipboardList,
    faUniversity,
    faBank
} from "@fortawesome/free-solid-svg-icons";
import { Cta } from "../components/Cta";

// Animation variants
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.1 }
    }
};

const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.8,
            ease: [0.22, 1, 0.36, 1]
        }
    }
};

const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.8, ease: "easeOut" }
    }
};

const bankingServices = [
    // {
    //     icon: faFileContract,
    //     title: "Loan Documentation",
    //     description: "Drafting, reviewing, and negotiating comprehensive loan agreements and security documents.",
    //     color: "gold",
    //     subservices: [
    //         "Term Loan Agreements",
    //         "Working Capital Facilities",
    //         "Project Finance Documents",
    //         "Syndicated Loan Agreements"
    //     ],
    //     stat: "500+"
    // },
    {
        icon: faGavel,
        title: "Debt Recovery",
        description: "Legal proceedings for recovery of bank loans through DRT, civil courts, and SARFAESI.",
        color: "primary-blue",
        subservices: [
            "DRT Proceedings",
            "SARFAESI Actions",
            "Civil Suits for Recovery",
            "One-time Settlements"
        ],
        stat: "300+"
    },
    // {
    //     icon: faShieldAlt,
    //     title: "Regulatory Compliance",
    //     description: "Advisory on RBI regulations, banking laws, and regulatory compliance matters.",
    //     color: "gold",
    //     subservices: [
    //         "RBI Compliance Advisory",
    //         "KYC/AML Compliance",
    //         "Basel Norms Implementation",
    //         "Regulatory Reporting"
    //     ],
    //     stat: "200+"
    // },
    {
        icon: faScaleBalanced,
        title: "Banking Litigation",
        description: "Representation in banking disputes, fraud cases, and regulatory investigations.",
        color: "primary-blue",
        subservices: [
            "Bank Fraud Cases",
            "Cheque Bounce Matters",
            "Bank Guarantee Disputes",
            "Consumer Banking Cases"
        ],
        stat: "150+"
    },
    // {
    //     icon: faHandshake,
    //     title: "Structured Finance",
    //     description: "Legal support for complex financing structures and financial products.",
    //     color: "gold",
    //     subservices: [
    //         "Securitization Transactions",
    //         "Structured Products",
    //         "Derivatives Documentation",
    //         "Trade Finance"
    //     ],
    //     stat: "100+"
    // },
    // {
    //     icon: faGlobe,
    //     title: "International Banking",
    //     description: "Cross-border banking transactions, foreign currency loans, and international regulations.",
    //     color: "primary-blue",
    //     subservices: [
    //         "ECB Compliance",
    //         "Foreign Currency Loans",
    //         "Cross-border Security",
    //         "International Regulations"
    //     ],
    //     stat: "75+"
    // }
];

const securitizationServices = [
    {
        service: "Asset Securitization",
        icon: faChartLine,
        description: "Structuring and documentation of asset-backed securities and securitization transactions.",
        features: ["Loan Pool Securitization", "ABS Structuring", "Credit Enhancement", "Rating Agency Liaison"],
        color: "gold"
    },
    {
        service: "SARFAESI Proceedings",
        icon: faLock,
        description: "Enforcement of security interests under SARFAESI Act for banks and financial institutions.",
        features: ["Possession Notices", "Auction Proceedings", "DRT Appeals", "Asset Reconstruction"],
        color: "primary-blue"
    },
    {
        service: "Asset Reconstruction",
        icon: faBuilding,
        description: "Legal support for asset reconstruction companies and stressed asset resolution.",
        features: ["ARC Advisory", "NPAs Acquisition", "Restructuring Plans", "One-time Settlements"],
        color: "gold"
    },
    {
        service: "Security Creation",
        icon: faFileSignature,
        description: "Creation, registration, and enforcement of various security interests.",
        features: ["Mortgage Documentation", "Charge Creation", "Security Trustee", "Collateral Management"],
        color: "primary-blue"
    }
];

const keyLegislations = [
    {
        act: "SARFAESI Act, 2002",
        icon: faLock,
        description: "Enables banks to enforce security interests without court intervention for NPA recovery.",
        keyProvisions: ["Possession Rights", "Asset Reconstruction", "Enforcement of Security", "DRT Appeals"]
    },
    {
        act: "RBI Act, 1934",
        icon: faBank,
        description: "Governs Reserve Bank of India's functions and banking regulation in India.",
        keyProvisions: ["Banking Regulation", "Monetary Policy", "Foreign Exchange", "Regulatory Powers"]
    },
    {
        act: "Banking Regulation Act, 1949",
        icon: faLandmark,
        description: "Regulates banking companies and provides framework for banking operations.",
        keyProvisions: ["Licensing", "Business Operations", "Inspection", "Winding Up"]
    },
    {
        act: "DRT Act, 1993",
        icon: faGavel,
        description: "Establishes Debt Recovery Tribunals for expeditious adjudication of recovery cases.",
        keyProvisions: ["Jurisdiction", "Recovery Proceedings", "Appeals Mechanism", "Enforcement"]
    },
    {
        act: "FEMA, 1999",
        icon: faGlobe,
        description: "Foreign Exchange Management Act governing cross-border transactions and investments.",
        keyProvisions: ["Foreign Investment", "External Commercial Borrowings", "NRI Accounts", "Compliance"]
    },
    {
        act: "Securitization Act",
        icon: faChartLine,
        description: "Framework for securitization and reconstruction of financial assets.",
        keyProvisions: ["Asset Reconstruction", "Security Enforcement", "SPV Formation", "Investor Protection"]
    }
];

const bankingSectors = [
    {
        sector: "Commercial Banks",
        icon: faBuilding,
        description: "Legal services for scheduled commercial banks and public sector banks.",
        clients: ["HDFC Bank", "Public Sector Banks", "Private Banks"],
        color: "gold"
    },
    {
        sector: "NBFCs",
        icon: faMoneyBillWave,
        description: "Legal advisory for Non-Banking Financial Companies and fintech lenders.",
        clients: ["NBFCs", "Fintech Companies", "Digital Lenders"],
        color: "primary-blue"
    },
    {
        sector: "Asset Reconstruction",
        icon: faChartLine,
        description: "Legal support for ARCs and stressed asset resolution professionals.",
        clients: ["ARCs", "Asset Managers", "Investors in NPAs"],
        color: "gold"
    },
    {
        sector: "Microfinance",
        icon: faUsers,
        description: "Legal services for microfinance institutions and small finance banks.",
        clients: ["MFIs", "Small Finance Banks", "Financial Inclusion Entities"],
        color: "primary-blue"
    }
];

const caseStudies = [
    {
        title: "₹250 Crore NPA Recovery",
        client: "Major Public Sector Bank",
        issue: "Stressed corporate loan with multiple securities",
        solution: "SARFAESI proceedings and strategic settlement",
        result: "90% recovery achieved",
        duration: "8 months",
        icon: faMoneyBillWave
    },
    {
        title: "Structured Finance Transaction",
        client: "Private Bank",
        issue: "₹500 crore loan pool securitization",
        solution: "Comprehensive legal structuring and documentation",
        result: "Successful securitization completed",
        duration: "4 months",
        icon: faChartLine
    },
    {
        title: "Bank Fraud Investigation",
        client: "Private Sector Bank",
        issue: "₹50 crore corporate banking fraud",
        solution: "Comprehensive legal defense and investigation",
        result: "Successful resolution",
        duration: "12 months",
        icon: faShieldAlt
    },
    {
        title: "DRT Litigation Success",
        client: "Financial Institution",
        issue: "Complex recovery case with multiple defendants",
        solution: "Strategic DRT litigation approach",
        result: "Favorable decree obtained",
        duration: "18 months",
        icon: faGavel
    }
];

const recoveryMethods = [
    {
        method: "SARFAESI Action",
        description: "Direct enforcement of security without court intervention",
        timeFrame: "3–6 months",
        successRate: "Very High",
        icon: faLock,
        color: "gold"
    },
    {
        method: "DRT Proceedings",
        description: "Recovery through Debt Recovery Tribunal",
        timeFrame: "12–24 months",
        successRate: "High",
        icon: faGavel,
        color: "primary-blue"
    },
    {
        method: "Civil Suits",
        description: "Traditional recovery through civil courts",
        timeFrame: "24–36 months",
        successRate: "Moderate",
        icon: faScaleBalanced,
        color: "gold"
    },
    {
        method: "Settlement Negotiations",
        description: "One-time settlements and restructuring",
        timeFrame: "1–3 months",
        successRate: "High",
        icon: faHandshake,
        color: "primary-blue"
    }
];


export const BankingSecuritization = () => {
    const [activeService, setActiveService] = useState(0);
    const [activeMethod, setActiveMethod] = useState(0);

    return (
        <div style={{
            backgroundColor: "var(--cream)",
            minHeight: "100vh",
            padding: "130px 0 60px",
            position: "relative"
        }}>
            {/* Background decorative elements */}
            <div className="position-fixed top-0 start-0 w-100 h-100" style={{ zIndex: 0, pointerEvents: "none", overflow: "hidden" }}>
                <div className="position-absolute top-0 end-0 w-25 h-25"
                    style={{
                        background: "radial-gradient(circle, var(--gold) 0%, transparent 70%)",
                        opacity: 0.03,
                        transform: "translate(30%, -30%)"
                    }}
                ></div>
                <div className="position-absolute bottom-0 start-0 w-30 h-30"
                    style={{
                        background: "radial-gradient(circle, var(--primary-blue) 0%, transparent 70%)",
                        opacity: 0.03,
                        transform: "translate(-30%, 30%)"
                    }}
                ></div>
            </div>

            <div className="container position-relative" style={{ zIndex: 1 }}>
                {/* Hero Section */}
                <motion.div
                    className="text-center mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <motion.div variants={fadeInUp}>
                        <span className="d-inline-block px-4 py-2 rounded-pill mb-3"
                            style={{
                                color: "var(--gold)",
                                fontWeight: "600",
                                letterSpacing: "2px",
                                fontSize: "0.9rem",
                                textTransform: "uppercase",
                                border: "1px solid var(--gold-light)",
                                background: "rgba(184, 134, 11, 0.05)",
                                backdropFilter: "blur(4px)"
                            }}
                        >
                            Financial Legal Specialists
                        </span>



                        <motion.h4
                            className="fw-bold display-6 display-md-5 mt-2"
                            variants={fadeInUp}
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                fontWeight: 600,
                                color: 'var(--primary-dark)',
                                background: "linear-gradient(135deg, var(--primary-dark) 0%, var(--gold) 100%)",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                backgroundClip: "text"
                            }}
                        >
                            Financial Legal Specialists Securitization Law
                        </motion.h4>
                    </motion.div>

                    <motion.p
                        className="fs-6 fs-md-5 mx-auto mb-3 mb-md-4"
                        variants={fadeInUp}
                        style={{
                            maxWidth: "750px",
                            color: "var(--text-primary)",
                            lineHeight: 1.7
                        }}
                    >
                        Expert legal solutions for banks, financial institutions, and securitization
                        transactions. Our Team provide strategic
                        counsel for banking operations, debt recovery, and financial regulations.
                    </motion.p>
                </motion.div>

                {/* Banking Services */}
                <motion.div
                    className="mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <div className="text-center mb-5">
                        <h2 className="fw-bold mb-3"
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                color: "var(--primary-dark)"
                            }}
                        >
                            <FontAwesomeIcon icon={faBank} className="me-3" style={{ color: "var(--gold)" }} />
                            Banking Law Services
                        </h2>
                        <p className="text-muted" style={{ maxWidth: "700px", margin: "0 auto" }}>
                            Comprehensive legal support for banking operations and financial transactions
                        </p>
                    </div>

                    <div className="row g-4 d-flex justify-content-center">
                        {bankingServices.map((service, index) => (
                            <motion.div
                                key={index}
                                className="col-md-6 col-lg-4"
                                variants={itemVariants}
                                onMouseEnter={() => setActiveService(index)}
                            >
                                <motion.div
                                    className="h-100 p-4 rounded-4 position-relative overflow-hidden"
                                    style={{
                                        backgroundColor: "var(--white)",
                                        border: `1px solid var(--${service.color}-light)`,
                                        boxShadow: "var(--shadow-soft)",
                                        transition: "var(--transition)"
                                    }}
                                    whileHover={{
                                        y: -8,
                                        boxShadow: "var(--shadow-medium)",
                                        borderColor: `var(--${service.color})`
                                    }}
                                >
                                    <div className="position-relative z-1 h-100 d-flex flex-column">
                                        {/* Icon & Stat */}
                                        <div className="d-flex align-items-center justify-content-between mb-4">
                                            <motion.div
                                                className="p-3 rounded-3"
                                                style={{
                                                    backgroundColor: `rgba(var(--${service.color}-rgb), 0.1)`,
                                                    color: `var(--${service.color})`
                                                }}
                                                whileHover={{ rotate: 360 }}
                                                transition={{ duration: 0.6, ease: "easeInOut" }}
                                            >
                                                <FontAwesomeIcon icon={service.icon} style={{ fontSize: "1.5rem" }} />
                                            </motion.div>

                                            {/* <motion.div
                                                className="display-6 fw-bold"
                                                style={{
                                                    color: `var(--${service.color})`,
                                                    fontFamily: "'Playfair Display', serif"
                                                }}
                                                animate={{
                                                    scale: activeService === index ? 1.1 : 1
                                                }}
                                                transition={{ type: "spring", stiffness: 300 }}
                                            >
                                                {service.stat}
                                            </motion.div> */}
                                        </div>

                                        {/* Title & Description */}
                                        <h5 className="fw-bold mb-3" style={{ color: "var(--primary-dark)" }}>
                                            {service.title}
                                        </h5>

                                        <p className="mb-4 flex-grow-1 small"
                                            style={{
                                                color: "var(--text-primary)",
                                                lineHeight: 1.6
                                            }}
                                        >
                                            {service.description}
                                        </p>

                                        {/* Subservices */}
                                        <div className="mt-auto pt-3 border-top"
                                            style={{ borderColor: `var(--${service.color}-light)` }}
                                        >
                                            <div className="vstack gap-2">
                                                {service.subservices.map((subservice, idx) => (
                                                    <motion.div
                                                        key={idx}
                                                        className="d-flex align-items-center gap-2"
                                                        initial={{ opacity: 0, x: -10 }}
                                                        whileInView={{ opacity: 1, x: 0 }}
                                                        viewport={{ once: true }}
                                                        transition={{ delay: idx * 0.1 }}
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={faChevronRight}
                                                            style={{
                                                                color: `var(--${service.color})`,
                                                                fontSize: "0.7rem"
                                                            }}
                                                        />
                                                        <span className="small" style={{ color: "var(--text-primary)" }}>
                                                            {subservice}
                                                        </span>
                                                    </motion.div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* Securitization Services */}
                {/* <motion.div
                    className="mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <div className="text-center mb-5">
                        <h2 className="fw-bold mb-3"
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                color: "var(--primary-dark)"
                            }}
                        >
                            <FontAwesomeIcon icon={faChartLine} className="me-3" style={{ color: "var(--gold)" }} />
                            Securitization & Recovery Services
                        </h2>
                        <p className="text-muted" style={{ maxWidth: "700px", margin: "0 auto" }}>
                            Specialized legal services for asset securitization and debt recovery
                        </p>
                    </div>

                    <div className="row g-4">
                        {securitizationServices.map((service, index) => (
                            <motion.div
                                key={index}
                                className="col-md-6 col-lg-3"
                                variants={itemVariants}
                            >
                                <motion.div
                                    className="p-4 rounded-4 h-100"
                                    style={{
                                        backgroundColor: "var(--white)",
                                        border: `2px solid var(--${service.color}-light)`,
                                        boxShadow: "var(--shadow-soft)",
                                        transition: "var(--transition)"
                                    }}
                                    whileHover={{
                                        y: -5,
                                        boxShadow: "var(--shadow-medium)",
                                        borderColor: `var(--${service.color})`
                                    }}
                                >
                                    <div className="text-center mb-3">
                                        <div className="p-3 rounded-circle d-inline-block"
                                            style={{
                                                backgroundColor: `rgba(var(--${service.color}-rgb), 0.1)`,
                                                color: `var(--${service.color})`
                                            }}
                                        >
                                            <FontAwesomeIcon icon={service.icon} style={{ fontSize: "1.5rem" }} />
                                        </div>
                                    </div>

                                    <h5 className="fw-bold text-center mb-3" style={{ color: "var(--primary-dark)" }}>
                                        {service.service}
                                    </h5>

                                    <p className="small text-center mb-4" style={{
                                        color: "var(--text-primary)",
                                        lineHeight: 1.6
                                    }}>
                                        {service.description}
                                    </p>

                                    <div className="vstack gap-2">
                                        {service.features.map((feature, idx) => (
                                            <div key={idx} className="d-flex align-items-center gap-2">
                                                <FontAwesomeIcon
                                                    icon={faCheckCircle}
                                                    style={{
                                                        color: `var(--${service.color})`,
                                                        fontSize: "0.7rem"
                                                    }}
                                                />
                                                <span className="small" style={{ color: "var(--text-primary)" }}>
                                                    {feature}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </motion.div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div> */}

                {/* Recovery Methods */}
                <motion.div
                    className="mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <div className="text-center mb-5">
                        <h2 className="fw-bold mb-3"
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                color: "var(--primary-dark)"
                            }}
                        >
                            <FontAwesomeIcon icon={faMoneyBillWave} className="me-3" style={{ color: "var(--gold)" }} />
                            Debt Recovery Methods
                        </h2>
                        <p className="text-muted" style={{ maxWidth: "700px", margin: "0 auto" }}>
                            Strategic approaches for effective debt recovery and NPA resolution
                        </p>
                    </div>

                    <div className="d-flex flex-wrap justify-content-center gap-3 mb-4">
                        {recoveryMethods.map((method, index) => (
                            <motion.button
                                key={index}
                                className={`btn px-4 py-3 rounded-pill d-flex align-items-center gap-3 ${activeMethod === index ? 'active' : ''}`}
                                onClick={() => setActiveMethod(index)}
                                variants={itemVariants}
                                style={{
                                    backgroundColor: activeMethod === index ? `var(--${method.color})` : "transparent",
                                    color: activeMethod === index ? "var(--white)" : `var(--${method.color})`,
                                    border: `2px solid var(--${method.color})`,
                                    fontWeight: "600",
                                    transition: "var(--transition)",
                                    minWidth: "220px"
                                }}
                                whileHover={{
                                    scale: 1.05,
                                    backgroundColor: activeMethod === index ? `var(--${method.color}-light)` : `rgba(var(--${method.color}-rgb), 0.1)`
                                }}
                                whileTap={{ scale: 0.98 }}
                            >
                                <FontAwesomeIcon icon={method.icon} />
                                <span>{method.method}</span>
                            </motion.button>
                        ))}
                    </div>

                    {/* Method Details */}
                    <motion.div
                        className="p-4 p-md-5 rounded-4"
                        style={{
                            backgroundColor: "var(--white)",
                            border: `2px solid var(--${recoveryMethods[activeMethod].color})`,
                            boxShadow: "var(--shadow-medium)"
                        }}
                        key={activeMethod}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <div className="row g-4">
                            <div className="col-lg-8">
                                <div className="d-flex align-items-center gap-3 mb-4">
                                    <div className="p-3 rounded-3"
                                        style={{
                                            backgroundColor: `rgba(var(--${recoveryMethods[activeMethod].color}-rgb), 0.1)`,
                                            color: `var(--${recoveryMethods[activeMethod].color})`
                                        }}
                                    >
                                        <FontAwesomeIcon icon={recoveryMethods[activeMethod].icon} style={{ fontSize: "2rem" }} />
                                    </div>
                                    <div>
                                        <h3 className="fw-bold mb-2" style={{ color: "var(--primary-dark)" }}>
                                            {recoveryMethods[activeMethod].method}
                                        </h3>
                                        <p className="mb-0" style={{ color: "var(--text-primary)" }}>
                                            {recoveryMethods[activeMethod].description}
                                        </p>
                                    </div>
                                </div>

                                <div className="row g-4">
                                    <div className="col-md-6">
                                        <div className="p-3 rounded-3"
                                            style={{
                                                backgroundColor: "rgba(var(--gold-rgb), 0.05)",
                                                border: "1px solid var(--gold-light)"
                                            }}
                                        >
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <div className="small" style={{ color: "var(--text-primary)" }}>Time Frame</div>
                                                    <div className="fw-bold" style={{ color: "var(--gold)" }}>
                                                        {recoveryMethods[activeMethod].timeFrame}
                                                    </div>
                                                </div>
                                                <FontAwesomeIcon icon={faCalendarAlt} style={{ color: "var(--gold)" }} />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <div className="p-3 rounded-3"
                                            style={{
                                                backgroundColor: "rgba(var(--primary-blue-rgb), 0.05)",
                                                border: "1px solid var(--primary-blue)"
                                            }}
                                        >
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <div className="small" style={{ color: "var(--text-primary)" }}>Success Rate</div>
                                                    <div className="fw-bold" style={{ color: "var(--primary-blue)" }}>
                                                        {recoveryMethods[activeMethod].successRate}
                                                    </div>
                                                </div>
                                                <FontAwesomeIcon icon={faChartLine} style={{ color: "var(--primary-blue)" }} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-lg-4">
                                <div className="p-4 rounded-4 h-100"
                                    style={{
                                        backgroundColor: `rgba(var(--${recoveryMethods[activeMethod].color}-rgb), 0.05)`,
                                        border: `1px solid var(--${recoveryMethods[activeMethod].color}-light)`
                                    }}
                                >
                                    <h6 className="fw-bold mb-3" style={{ color: `var(--${recoveryMethods[activeMethod].color})` }}>
                                        Key Advantages
                                    </h6>
                                    <div className="vstack gap-3">
                                        {[
                                            "Legal Expertise",
                                            "Strategic Approach",
                                            "Time Efficiency",
                                            "Cost Effectiveness"
                                        ].map((advantage, idx) => (
                                            <div key={idx} className="d-flex align-items-center gap-2">
                                                <FontAwesomeIcon
                                                    icon={faStar}
                                                    style={{
                                                        color: `var(--${recoveryMethods[activeMethod].color})`,
                                                        fontSize: "0.8rem"
                                                    }}
                                                />
                                                <span className="small" style={{ color: "var(--text-primary)" }}>
                                                    {advantage}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>

                {/* Banking Sectors */}
                {/* <motion.div
                    className="mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <div className="text-center mb-5">
                        <h2 className="fw-bold mb-3"
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                color: "var(--primary-dark)"
                            }}
                        >
                            <FontAwesomeIcon icon={faUsers} className="me-3" style={{ color: "var(--gold)" }} />
                            Banking Sector Expertise
                        </h2>
                        <p className="text-muted" style={{ maxWidth: "700px", margin: "0 auto" }}>
                            Legal services tailored to different banking and financial sectors
                        </p>
                    </div>

                    <div className="row g-4">
                        {bankingSectors.map((sector, index) => (
                            <motion.div
                                key={index}
                                className="col-md-6 col-lg-3"
                                variants={itemVariants}
                            >
                                <motion.div
                                    className="p-4 rounded-4 h-100 text-center"
                                    style={{
                                        backgroundColor: "var(--white)",
                                        border: `2px solid var(--${sector.color}-light)`,
                                        boxShadow: "var(--shadow-soft)",
                                        transition: "var(--transition)"
                                    }}
                                    whileHover={{
                                        y: -5,
                                        boxShadow: "var(--shadow-medium)",
                                        borderColor: `var(--${sector.color})`
                                    }}
                                >
                                    <motion.div
                                        className="mb-3"
                                        whileHover={{ scale: 1.1, rotate: 5 }}
                                    >
                                        <div className="p-3 rounded-circle d-inline-block"
                                            style={{
                                                backgroundColor: `rgba(var(--${sector.color}-rgb), 0.1)`,
                                                color: `var(--${sector.color})`
                                            }}
                                        >
                                            <FontAwesomeIcon icon={sector.icon} style={{ fontSize: "1.5rem" }} />
                                        </div>
                                    </motion.div>

                                    <h5 className="fw-bold mb-3" style={{ color: "var(--primary-dark)" }}>
                                        {sector.sector}
                                    </h5>

                                    <p className="small mb-4" style={{
                                        color: "var(--text-primary)",
                                        lineHeight: 1.6
                                    }}>
                                        {sector.description}
                                    </p>

                                    <div className="pt-3 border-top"
                                        style={{ borderColor: `var(--${sector.color}-light)` }}
                                    >
                                        <div className="small fw-bold mb-1" style={{ color: "var(--primary-dark)" }}>
                                            Representative Clients:
                                        </div>
                                        <div className="small" style={{ color: "var(--gold)" }}>
                                            {sector.clients.join(", ")}
                                        </div>
                                    </div>
                                </motion.div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div> */}

                {/* Key Legislations */}
                <motion.div
                    className="mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <div className="text-center mb-5">
                        <h2 className="fw-bold mb-3"
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                color: "var(--primary-dark)"
                            }}
                        >
                            <FontAwesomeIcon icon={faGavel} className="me-3" style={{ color: "var(--gold)" }} />
                            Key Banking Legislations
                        </h2>
                        <p className="text-muted" style={{ maxWidth: "700px", margin: "0 auto" }}>
                            Expertise in major banking and financial legislation
                        </p>
                    </div>

                    <div className="row g-4">
                        {keyLegislations.map((act, index) => (
                            <motion.div
                                key={index}
                                className="col-md-6 col-lg-4"
                                variants={itemVariants}
                            >
                                <motion.div
                                    className="p-4 rounded-4 h-100"
                                    style={{
                                        backgroundColor: "var(--white)",
                                        border: "1px solid var(--gold-light)",
                                        boxShadow: "var(--shadow-soft)",
                                        transition: "var(--transition)"
                                    }}
                                    whileHover={{
                                        y: -5,
                                        boxShadow: "var(--shadow-medium)"
                                    }}
                                >
                                    <div className="d-flex align-items-start gap-3 mb-3">
                                        <div className="p-2 rounded-3 flex-shrink-0"
                                            style={{
                                                backgroundColor: "rgba(var(--gold-rgb), 0.1)",
                                                color: "var(--gold)"
                                            }}
                                        >
                                            <FontAwesomeIcon icon={act.icon} />
                                        </div>
                                        <div>
                                            <h6 className="fw-bold mb-2" style={{ color: "var(--primary-dark)" }}>
                                                {act.act}
                                            </h6>
                                            <div className="small" style={{ color: "var(--text-primary)", lineHeight: 1.5 }}>
                                                {act.description}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-3 pt-3 border-top"
                                        style={{ borderColor: "var(--gold-light)" }}
                                    >
                                        <div className="vstack gap-2">
                                            {act.keyProvisions.map((provision, idx) => (
                                                <div key={idx} className="d-flex align-items-center gap-2">
                                                    <FontAwesomeIcon
                                                        icon={faChevronRight}
                                                        style={{
                                                            color: "var(--gold)",
                                                            fontSize: "0.7rem"
                                                        }}
                                                    />
                                                    <span className="small" style={{ color: "var(--text-primary)" }}>
                                                        {provision}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </motion.div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* Case Studies */}
                {/* <motion.div
                    className="mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <div className="text-center mb-5">
                        <h2 className="fw-bold mb-3"
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                color: "var(--primary-dark)"
                            }}
                        >
                            <FontAwesomeIcon icon={faAward} className="me-3" style={{ color: "var(--gold)" }} />
                            Banking Law Case Studies
                        </h2>
                        <p className="text-muted" style={{ maxWidth: "700px", margin: "0 auto" }}>
                            Successful resolutions in complex banking matters
                        </p>
                    </div>

                    <div className="row g-4">
                        {caseStudies.map((caseStudy, index) => (
                            <motion.div
                                key={index}
                                className="col-md-6 col-lg-3"
                                variants={itemVariants}
                            >
                                <motion.div
                                    className="p-4 rounded-4 h-100"
                                    style={{
                                        backgroundColor: "var(--white)",
                                        border: "1px solid var(--gold-light)",
                                        boxShadow: "var(--shadow-soft)",
                                        transition: "var(--transition)"
                                    }}
                                    whileHover={{
                                        y: -5,
                                        boxShadow: "var(--shadow-medium)",
                                        borderColor: "var(--gold)"
                                    }}
                                >
                                    <div className="mb-3">
                                        <div className="d-flex align-items-center gap-2 mb-2">
                                            <div className="p-2 rounded-3 flex-shrink-0"
                                                style={{
                                                    backgroundColor: "rgba(var(--gold-rgb), 0.1)",
                                                    color: "var(--gold)"
                                                }}
                                            >
                                                <FontAwesomeIcon icon={caseStudy.icon} />
                                            </div>
                                            <h6 className="fw-bold mb-0" style={{ color: "var(--primary-dark)" }}>
                                                {caseStudy.title}
                                            </h6>
                                        </div>
                                        <div className="small mb-3" style={{ color: "var(--gold)" }}>
                                            Client: {caseStudy.client}
                                        </div>
                                    </div>

                                    <div className="vstack gap-3 mb-4">
                                        <div>
                                            <div className="small fw-bold mb-1" style={{ color: "var(--primary-dark)" }}>
                                                Issue
                                            </div>
                                            <div className="small" style={{ color: "var(--text-primary)" }}>
                                                {caseStudy.issue}
                                            </div>
                                        </div>

                                        <div>
                                            <div className="small fw-bold mb-1" style={{ color: "var(--primary-dark)" }}>
                                                Solution
                                            </div>
                                            <div className="small" style={{ color: "var(--text-primary)" }}>
                                                {caseStudy.solution}
                                            </div>
                                        </div>

                                        <div>
                                            <div className="small fw-bold mb-1" style={{ color: "var(--primary-dark)" }}>
                                                Result
                                            </div>
                                            <div className="small fw-bold" style={{ color: "var(--green-accent)" }}>
                                                {caseStudy.result}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="d-flex justify-content-between align-items-center pt-3 border-top"
                                        style={{ borderColor: "var(--gold-light)" }}
                                    >
                                        <div className="d-flex align-items-center gap-1">
                                            <FontAwesomeIcon
                                                icon={faCalendarAlt}
                                                style={{
                                                    color: "var(--primary-blue)",
                                                    fontSize: "0.8rem"
                                                }}
                                            />
                                            <span className="small" style={{ color: "var(--text-primary)" }}>
                                                {caseStudy.duration}
                                            </span>
                                        </div>
                                    </div>
                                </motion.div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div> */}

                {/* Our Expertise */}
                <motion.div
                    className="mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <div className="p-4 p-md-5 rounded-4"
                        style={{
                            backgroundColor: "var(--primary-dark)",
                            backgroundImage: "linear-gradient(135deg, var(--primary-dark) 0%, var(--primary-blue) 100%)",
                            border: "1px solid var(--gold-light)"
                        }}
                    >
                        <div className="row align-items-center">
                            <div className="col-lg-8">
                                <motion.div variants={fadeInUp}>
                                    <h3 className="fw-bold mb-3" style={{ color: "var(--white)" }}>
                                        <FontAwesomeIcon icon={faUserTie} className="me-3" style={{ color: "var(--gold)" }} />
                                        Our Banking Law Expertise
                                    </h3>

                                    <div className="row g-4 mb-4">
                                        {/* <div className="col-md-6">
                                            <div className="d-flex align-items-start gap-3 mb-3">
                                                <FontAwesomeIcon
                                                    icon={faUniversity}
                                                    style={{
                                                        color: "var(--gold)",
                                                        fontSize: "1.2rem",
                                                        marginTop: "2px"
                                                    }}
                                                />
                                                <div>
                                                    <div className="fw-bold mb-1" style={{ color: "var(--white)" }}>
                                                        London-Educated Experts
                                                    </div>
                                                    <div className="small" style={{ color: "rgba(255, 255, 255, 0.8)" }}>
                                                        International banking law perspective
                                                    </div>
                                                </div>
                                            </div>
                                        </div> */}

                                        {/* <div className="col-md-6">
                                            <div className="d-flex align-items-start gap-3 mb-3">
                                                <FontAwesomeIcon
                                                    icon={faShieldAlt}
                                                    style={{
                                                        color: "var(--gold)",
                                                        fontSize: "1.2rem",
                                                        marginTop: "2px"
                                                    }}
                                                />
                                                <div>
                                                    <div className="fw-bold mb-1" style={{ color: "var(--white)" }}>
                                                        Regulatory Compliance
                                                    </div>
                                                    <div className="small" style={{ color: "rgba(255, 255, 255, 0.8)" }}>
                                                        Expertise in RBI and banking regulations
                                                    </div>
                                                </div>
                                            </div>
                                        </div> */}

                                        <div className="col-md-6">
                                            <div className="d-flex align-items-start gap-3 mb-3">
                                                <FontAwesomeIcon
                                                    icon={faChartLine}
                                                    style={{
                                                        color: "var(--gold)",
                                                        fontSize: "1.2rem",
                                                        marginTop: "2px"
                                                    }}
                                                />
                                                <div>
                                                    <div className="fw-bold mb-1" style={{ color: "var(--white)" }}>
                                                        Securitization Specialists
                                                    </div>
                                                    <div className="small" style={{ color: "rgba(255, 255, 255, 0.8)" }}>
                                                        Complex financial structuring expertise
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="d-flex align-items-start gap-3 mb-3">
                                                <FontAwesomeIcon
                                                    icon={faGavel}
                                                    style={{
                                                        color: "var(--gold)",
                                                        fontSize: "1.2rem",
                                                        marginTop: "2px"
                                                    }}
                                                />
                                                <div>
                                                    <div className="fw-bold mb-1" style={{ color: "var(--white)" }}>
                                                        Recovery & Enforcement
                                                    </div>
                                                    <div className="small" style={{ color: "rgba(255, 255, 255, 0.8)" }}>
                                                        SARFAESI and DRT expertise
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* <div className="row g-4">
                                        <div className="col-md-4">
                                            <div className="text-center">
                                                <div className="display-4 fw-bold mb-1" style={{ color: "var(--gold)" }}>
                                                    500+
                                                </div>
                                                <div className="small" style={{ color: "rgba(255, 255, 255, 0.8)" }}>
                                                    Loan Agreements
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="text-center">
                                                <div className="display-4 fw-bold mb-1" style={{ color: "var(--gold)" }}>
                                                    300+
                                                </div>
                                                <div className="small" style={{ color: "rgba(255, 255, 255, 0.8)" }}>
                                                    Recovery Cases
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="text-center">
                                                <div className="display-4 fw-bold mb-1" style={{ color: "var(--gold)" }}>
                                                    85%
                                                </div>
                                                <div className="small" style={{ color: "rgba(255, 255, 255, 0.8)" }}>
                                                    Success Rate
                                                </div>
                                            </div>
                                        </div>
                                    </div> */}
                                </motion.div>
                            </div>

                            <div className="col-lg-4">
                                <motion.div
                                    className="text-center"
                                    variants={itemVariants}
                                    whileHover={{ scale: 1.05 }}
                                    transition={{ type: "spring", stiffness: 300 }}
                                >
                                    <div className="p-4 rounded-4 d-inline-block"
                                        style={{
                                            backgroundColor: "rgba(184, 134, 11, 0.15)",
                                            border: "2px solid var(--gold)",
                                            backdropFilter: "blur(10px)"
                                        }}
                                    >
                                        <FontAwesomeIcon
                                            icon={faLandmark}
                                            className="mb-3"
                                            style={{ fontSize: "3rem", color: "var(--gold)" }}
                                        />
                                        <div className="fw-bold mb-2" style={{ color: "var(--white)", fontSize: "1.2rem" }}>
                                            Banking Law Specialists
                                        </div>
                                        <div style={{ color: "var(--gold)" }}>
                                            Trusted by Financial Institutions
                                        </div>
                                    </div>
                                </motion.div>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* CTA Section */}
                
                <Cta  Title="Need Banking Legal Expertise?" description=" Contact our banking law specialists for strategic legal solutions for financial institutions and securitization transactions."/>
            </div>
        </div>
    );
};